/*eslint max-lines-per-function: ["error", 330]*/
import React, { useEffect, useState, useMemo } from 'react';

import FormRow from '../../../app/components/FormRow';
import Button from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import {
  CreateUpdateSalesInvoiceDtoStatusEnum,
  GetSalesInvoiceDto,
  GetSalesInvoiceDtoStatusKsefEnum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import {
  convertSalesInvoice,
  validate,
} from '../../../common/spdCore/contexts/SalesInvoiceContext';
import { useNavigation } from '../../../common/navigation';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { SalesInvoiceEditorTabs } from '../../containers/SalesInvoiceEditorView/SalesInvoiceEditorView';
import DragNDropFiles, {
  NewFile,
} from '../../../common/components/File/DragNDropFiles';
import { convertUploadedFilesToAttachments } from '../../../common/components/File/DragNDropUtils';
import { AttachmentType } from '../../../common/spdCore/validation/schemas';

import { CorrectionReasonSelector } from './CorrectionReasonSelector';
import { SalesInvoiceStatusSelector } from './SalesInvoiceStatusSelector';

interface SalesInvoiceFinalizeProps {
  salesInvoice?: GetSalesInvoiceDto;
  salesInvoiceId?: string;
  handleRefresh: () => void;
  setNotSavedTab: (tab: SalesInvoiceEditorTabs | undefined) => void;
  readOnly?: boolean;
}

export const SalesInvoiceFinalize: React.FC<
  SalesInvoiceFinalizeProps
> = props => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const auth = useAuth();
  const canManageKsef = auth.check([Role.Admin, Role.Finance]);
  const [status, setStatus] = useState<string | undefined>(
    props.salesInvoice?.status,
  );
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isRevertToDraftLoading, setIsRevertToDraftLoading] = useState(false);
  const [isRetryKsefLoading, setIsRetryKsefLoading] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [correctionReasonTxt, setCorrectionReasonTxt] = useState<
    string | undefined
  >(props.salesInvoice?.correctionReasonTxt ?? undefined);
  const [additionalNotesTxt, setAdditionalNotesTxt] = useState<
    string | undefined
  >(props.salesInvoice?.additionalNotesTxt ?? undefined);
  const [filesToAdd, setFilesToAdd] = useState<NewFile[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);
  const [maxFileSizeExceeded, setMaxFileSizeExceeded] = useState(false);

  const existingFiles = useMemo(
    () =>
      props.salesInvoice?.attachments?.map(file => ({
        id: file.id,
        name: file.name || 'file',
        size: Number(file.fileSize) || 0,
      })) || [],
    [props.salesInvoice?.attachments],
  );

  const isCorrection = !!props.salesInvoice?.originalInvoice?.id;

  const isInvoiceDateToday = useMemo(() => {
    if (!props.salesInvoice?.invoiceDate) {
      return false;
    }
    const invoiceDate = new Date(props.salesInvoice.invoiceDate);
    const today = new Date();
    return (
      invoiceDate.getFullYear() === today.getFullYear() &&
      invoiceDate.getMonth() === today.getMonth() &&
      invoiceDate.getDate() === today.getDate()
    );
  }, [props.salesInvoice?.invoiceDate]);

  const handleStatusChange = (newStatus: string | undefined) => {
    setStatus(newStatus);
    const changed = newStatus !== props.salesInvoice?.status;
    props.setNotSavedTab(changed ? SalesInvoiceEditorTabs.FINALIZE : undefined);
  };

  useEffect(() => {
    if (props.salesInvoice?.status) {
      setStatus(props.salesInvoice.status);
    }
    setCorrectionReasonTxt(
      props.salesInvoice?.correctionReasonTxt ?? undefined,
    );
    setAdditionalNotesTxt(props.salesInvoice?.additionalNotesTxt ?? undefined);
    setSaveErrors([]);
    setShowSaveErrors(false);
  }, [props.salesInvoice]);

  const handleSave = async () => {
    setIsSaveLoading(true);
    setSaveErrors([]);
    setShowSaveErrors(false);

    try {
      if (!props.salesInvoiceId || !props.salesInvoice) {
        setSaveErrors(['Brak danych faktury do zapisania']);
        setShowSaveErrors(true);
        return;
      }
      if (maxFileSizeExceeded) {
        setShowSaveErrors(true);
        setSaveErrors(['Przekroczono dozwolony rozmiar plików.']);
        return;
      }

      if (filesToAdd.length > 0 || filesToDelete.length > 0) {
        const attachments = convertUploadedFilesToAttachments(
          filesToAdd,
          filesToDelete,
          props.salesInvoiceId,
          AttachmentType.InvoiceAttachment,
        );

        await spd.attachments
          .addDeleteAttachments(attachments)
          .catch(errorResponse => {
            setShowSaveErrors(true);
            setSaveErrors(errorResponse.errors);
            setIsSaveLoading(false);
            return;
          });
      }

      const dto = convertSalesInvoice(props.salesInvoice);
      dto.status = status as CreateUpdateSalesInvoiceDtoStatusEnum;
      dto.correctionReasonTxt = correctionReasonTxt ?? null;
      dto.additionalNotesTxt = additionalNotesTxt ?? null;

      const validationResult = await validate(dto);
      if (!validationResult.valid) {
        setSaveErrors(validationResult.errors);
        setShowSaveErrors(true);
        return;
      }

      await spd.salesInvoices.update(props.salesInvoiceId, dto);
      props.setNotSavedTab(undefined);
      setFilesToAdd([]);
      setFilesToDelete([]);
      await props.handleRefresh();
    } catch (error) {
      const errors = await responseErrors(error as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!props.salesInvoiceId) {
      return;
    }

    setIsCancelLoading(true);
    setSaveErrors([]);
    setShowSaveErrors(false);

    try {
      await spd.salesInvoices.deactivate(props.salesInvoiceId);
      nav.salesInvoices();
    } catch (error) {
      console.error(error);
      const errors = await responseErrors(error as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleRevertToDraft = async () => {
    if (!props.salesInvoiceId) {
      return;
    }
    setIsRevertToDraftLoading(true);
    setSaveErrors([]);
    setShowSaveErrors(false);
    try {
      await spd.salesInvoices.revertToDraft(props.salesInvoiceId);
      await props.handleRefresh();
    } catch (error) {
      const errors = await responseErrors(error as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsRevertToDraftLoading(false);
    }
  };

  const handleRetryKsef = async () => {
    if (!props.salesInvoiceId) {
      return;
    }
    setIsRetryKsefLoading(true);
    setSaveErrors([]);
    setShowSaveErrors(false);
    try {
      await spd.salesInvoices.retryKsef(props.salesInvoiceId);
      await props.handleRefresh();
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    } finally {
      setIsRetryKsefLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      {showSaveErrors && (
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          onHide={() => setShowSaveErrors(false)}
        />
      )}
      {isCorrection && (
        <FormRow controlId="correctionReason" label="Przyczyna korekty">
          <CorrectionReasonSelector
            value={correctionReasonTxt}
            onChange={value => {
              setCorrectionReasonTxt(value);
              props.setNotSavedTab(SalesInvoiceEditorTabs.FINALIZE);
            }}
            readOnly={props.readOnly}
          />
        </FormRow>
      )}
      <FormRow controlId="additionalNotesTxt" label="Uwagi dodatkowe">
        <textarea
          className="form-control"
          value={additionalNotesTxt ?? ''}
          onChange={e => {
            setAdditionalNotesTxt(e.target.value || undefined);
            props.setNotSavedTab(SalesInvoiceEditorTabs.FINALIZE);
          }}
          readOnly={props.readOnly}
          disabled={props.readOnly}
          rows={3}
        />
      </FormRow>
      <FormRow controlId="status" label="Status">
        <SalesInvoiceStatusSelector
          value={status}
          onChange={value => handleStatusChange(value)}
          isDisabled={props.readOnly}
          withoutKseF={true}
        />
      </FormRow>
      <FormRow controlId="files" label="Załączniki">
        <DragNDropFiles
          existingFiles={existingFiles}
          setFilesToDelete={setFilesToDelete}
          setFilesToAdd={setFilesToAdd}
          setMaxFileSizeExceeded={setMaxFileSizeExceeded}
          onChange={() => props.setNotSavedTab(SalesInvoiceEditorTabs.FINALIZE)}
          readOnly={props.readOnly}
        />
      </FormRow>

      <div className="d-flex justify-content-between gap-2">
        {!props.readOnly && (
          <>
            <ConfirmationButton
              variant="danger"
              onOK={handleCancel}
              disabled={props.readOnly}
              isLoading={isCancelLoading}
              confirmation="Czy na pewno chcesz anulować tę fakturę?"
              confirmLabel="Tak"
              rejectLabel="Nie"
            >
              Anuluj fakturę
            </ConfirmationButton>
            <div className="d-flex justify-content-end gap-2 ms-auto">
              <Button
                onClick={handleSave}
                disabled={props.readOnly}
                isLoading={isSaveLoading}
              >
                Zapisz
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => nav.salesInvoices()}
              >
                Zamknij
              </Button>
            </div>
          </>
        )}
        {props.readOnly && (
          <div className="d-flex justify-content-end gap-2 ms-auto">
            {canManageKsef &&
              props.salesInvoice?.statusKsef ===
                GetSalesInvoiceDtoStatusKsefEnum.RejectedInKSeF && (
                <>
                  <ConfirmationButton
                    variant="warning"
                    onOK={handleRevertToDraft}
                    isLoading={isRevertToDraftLoading}
                    confirmation="Czy na pewno chcesz wycofać fakturę do szkicu?"
                    confirmLabel="Tak"
                    rejectLabel="Nie"
                  >
                    Wycofaj do szkicu
                  </ConfirmationButton>
                  {isInvoiceDateToday && (
                    <ConfirmationButton
                      variant="primary"
                      onOK={handleRetryKsef}
                      isLoading={isRetryKsefLoading}
                      confirmation="Czy na pewno chcesz ponownie wysłać fakturę do KSeF?"
                      confirmLabel="Tak"
                      rejectLabel="Nie"
                    >
                      Ponów wysyłkę do KSeF
                    </ConfirmationButton>
                  )}
                </>
              )}
            <Button
              variant="outline-secondary"
              onClick={() => nav.salesInvoices()}
            >
              Zamknij
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
